import { CreateForm } from 'framework/form';
import { AssignmentType, useAssignmentService } from 'domain/shared/assignment';
import { mapTeamStructureWithAssignments } from 'domain/shared/assignment/mapTeamStructureWithAssignments';
import { ProjectTeamType } from 'domain/shared/newProject';
import { SetStaffingProjectData, StaffingProjectAssignment, StaffingProjectData } from 'domain/shared/staffing';
import { useEmployeeService } from 'domain/shared/employee';
import CreateStaffingAssignmentModalContent from './CreateStaffingAssignmentModalContent';
import { useEffect } from 'react';
import { useMessageService } from 'framework/ui/Message';

export const STAFFING_ASSIGNMENT_MESSAGE = 'assignmentMessageContext';

interface CreateStaffingAssignmentModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  projectData: StaffingProjectData;
  setProjectData?: SetStaffingProjectData;
  initialData?: any;
  isPlanned?: boolean;
}

const CreateStaffingAssignmentModal = ({
  open,
  setOpen,
  projectData,
  setProjectData,
  initialData,
  isPlanned,
}: CreateStaffingAssignmentModalProps) => {
  const { create, getValidationSchema } = useAssignmentService();
  const { getById } = useEmployeeService();

  const { cleanMessage } = useMessageService(STAFFING_ASSIGNMENT_MESSAGE);

  useEffect(() => {
    if (open) {
      cleanMessage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <CreateForm
      initialData={{
        assignmentType: AssignmentType.INDIVIDUAL_ASSIGNMENT,
        ownerId: projectData.projectManager,
        bench: false,
        backup: false,
        isSpof: false,
        target: {
          id: projectData.legacyId,
          type: 'PROJECT',
          displayName: projectData.name,
        },
        ...initialData,
      }}
      submitFormFunc={async values => {
        const employee = values.employeeId ? await getById(values.employeeId) : null;
        const payload = {
          ...values,
          positionId: employee?.positionId,
          competencyLevel: employee?.specializationInfo?.competencyLevel,
          technologyId: employee?.specializationInfo?.technologyId,
        };
        const response = await create(payload);
        return { response, values: payload };
      }}
      afterSubmit={{
        execute: ({ response, values }) => {
          const nextAssignments = [
            ...((projectData.assignments || []) as StaffingProjectAssignment[]),
            {
              id: String(response?.id),
              employeeAlias: response?.employeeAlias,
              positionId: response?.positionId,
              technologyId: response?.technologyId,
              isBackup: response?.isBackup ?? false,
              isSpof: response?.isSpof ?? false,
              isBench: response?.isBench ?? false,
              projectId: projectData.id,
              projectName: projectData.name,
              fromDate: response?.fromDate,
              toDate: response?.toDate,
              maxPercent: response?.maxPercent,
              slotId: response?.slotId,
              status: response?.status,
              membershipRole: response?.membershipRole,
              competencyLevel: response?.competencyLevel,
              isProbation: response?.isProbation ?? false,
              noteText: values.noteText ?? null,
            },
          ];
          const nextProjectData = {
            ...projectData,
            assignments: nextAssignments,
          };
          const enhancedResponse = mapTeamStructureWithAssignments(
            nextProjectData,
            projectData.team as ProjectTeamType | undefined,
          );

          setProjectData?.({
            ...nextProjectData,
            ...enhancedResponse,
          });
          setOpen(false);
        },
        message: 'Assignment has been successfully created.',
      }}
      messageContext={STAFFING_ASSIGNMENT_MESSAGE}
      getValidationSchemaFunc={getValidationSchema}
    >
      <CreateStaffingAssignmentModalContent isVirtual={isPlanned} setOpen={setOpen} />
    </CreateForm>
  );
};

export default CreateStaffingAssignmentModal;
